import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Paper,
    IconButton,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Divider,
    alpha,
    useTheme,
    Tabs,
    Tab,
    Badge,
    Avatar,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    TablePagination,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    ShoppingCart as ShoppingCartIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Cancel as CancelIcon,
    TrendingUp as TrendingUpIcon,
    LocalShipping as LocalShippingIcon,
    MoreVert as MoreVertIcon,
    ThumbUp as ApproveIcon,
    Transform as ConvertIcon,
    Visibility as ViewIcon,
    Business as BusinessIcon,
    PersonOutline as PersonIcon,
    AccountBalance as AccountIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useAppSelector } from '../hooks/redux.hooks';
import { useConfirm } from '../hooks/useConfirm';
import PurchaseOrderDialog from '../components/purchaseOrders/PurchaseOrderDialog';
import PurchaseOrderPreviewDialog from '../components/purchaseOrders/PurchaseOrderPreviewDialog';
import { PurchaseOrder } from '../types/purchaseOrders.types';
import { purchaseOrdersService } from '../services/purchaseOrders.service';

const translations = {
    en: {
        title: 'Purchase Orders',
        addOrder: 'New Order',
        search: 'Search orders...',
        all: 'All',
        draft: 'Draft',
        approved: 'Approved',
        received: 'Received',
        cancelled: 'Cancelled',
        totalOrders: 'Total Orders',
        totalAmount: 'Total Amount',
        pendingOrders: 'Pending Orders',
        supplier: 'Supplier',
        custody: 'Custody',
        paymentAccount: 'Payment Account',
        warehouse: 'Warehouse',
        date: 'Order Date',
        delivery: 'Expected Delivery',
        amount: 'Amount',
        status: 'Status',
        actions: 'Actions',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        approve: 'Approve',
        complete: 'Mark Received',
        cancel: 'Cancel Order',
        convertToInvoice: 'Convert to Invoice',
        noData: 'No orders found',
        confirmDelete: 'Delete this order?',
        confirmApprove: 'Approve this order?',
        confirmComplete: 'Mark as received?',
        confirmCancel: 'Cancel this order?',
        convertConfirm: 'Convert this order to a Purchase Invoice?',
        paymentSource: 'Payment Source',
    },
    ar: {
        title: 'أوامر الشراء',
        addOrder: 'أمر جديد',
        search: 'بحث في الأوامر...',
        all: 'الكل',
        draft: 'مسودة',
        approved: 'معتمد',
        received: 'مستلم',
        cancelled: 'ملغي',
        totalOrders: 'إجمالي الأوامر',
        totalAmount: 'إجمالي المبالغ',
        pendingOrders: 'أوامر معلقة',
        supplier: 'المورد',
        custody: 'العهدة',
        paymentAccount: 'حساب الدفع',
        warehouse: 'المخزن',
        date: 'تاريخ الأمر',
        delivery: 'التسليم المتوقع',
        amount: 'المبلغ',
        status: 'الحالة',
        actions: 'الإجراءات',
        view: 'عرض',
        edit: 'تعديل',
        delete: 'حذف',
        approve: 'اعتماد',
        complete: 'تأكيد الاستلام',
        cancel: 'إلغاء',
        convertToInvoice: 'تحويل لفاتورة',
        noData: 'لا توجد أوامر',
        confirmDelete: 'حذف هذا الأمر؟',
        confirmApprove: 'اعتماد الأمر؟',
        confirmComplete: 'تأكيد استلام الأمر؟',
        confirmCancel: 'إلغاء الأمر؟',
        convertConfirm: 'تحويل الأمر إلى فاتورة شراء؟',
        paymentSource: 'مصدر الدفع',
    },
};

const PurchaseOrdersPage: React.FC = () => {
    const { language } = useLanguage();
    const theme = useTheme();
    const t = translations[language];
    const isRTL = language === 'ar';
    const { minDate, maxDate } = useFiscalDateRange();
    const confirm = useConfirm();

    const { userBranchIds, userWarehouseIds } = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
        const payload = JSON.parse(atob(token.split('.')[1]));
        const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
        const allowedBranches = payload.AllowedBranchId
          ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
          : [];
        const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
        const keeperClaims = payload.KeeperWarehouseId;
        const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
      } catch {
        return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      }
    })();
    
    const canEditOrder = (order: PurchaseOrder) => {
        if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
        if (order.warehouseId && userWarehouseIds.includes(order.warehouseId)) return true;
        if (order.branchId && userBranchIds.includes(order.branchId)) return true;
        return false;
    };
    
    const permissions = useAppSelector((state) => state.auth.permissions);
    const isSuperAdmin = useAppSelector((state) => state.auth.user?.isSuperAdmin);

    const canApproveOrder = () => {
        // Super Admin, or users with no branch restrictions, or users with PURCHASE_ORDER_CREATE
        return isSuperAdmin || (userBranchIds.length === 0 && userWarehouseIds.length === 0)
            || permissions?.includes('PURCHASE_ORDER_CREATE');
    };

    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<
        PurchaseOrder | undefined
    >();
    const [activeTab, setActiveTab] = useState(0);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOrderForMenu, setSelectedOrderForMenu] =
        useState<PurchaseOrder | null>(null);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [orderToConvert, setOrderToConvert] = useState<PurchaseOrder | null>(
        null
    );
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [orderToPreview, setOrderToPreview] = useState<PurchaseOrder | null>(
        null
    );
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        loadOrders();
    }, []);

    useEffect(() => {
        filterOrders();
    }, [searchTerm, orders, activeTab, filterDateFrom, filterDateTo]);

    useEffect(() => {
        setPage(0);
    }, [searchTerm, activeTab, filterDateFrom, filterDateTo]);

    const paginatedOrders = useMemo(
        () => filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredOrders, page, rowsPerPage]
    );

    const loadOrders = async () => {
        setLoading(true);
        try {
            const data = await purchaseOrdersService.getAll();
            const filteredData = data.filter((order: PurchaseOrder) => canEditOrder(order));
            setOrders(filteredData);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const filterOrders = () => {
        let filtered = orders;

        if (activeTab === 1) {
            filtered = filtered.filter((order) => order.status === 'Draft');
        } else if (activeTab === 2) {
            filtered = filtered.filter((order) => order.status === 'Approved');
        } else if (activeTab === 3) {
            filtered = filtered.filter((order) => order.status === 'Received');
        } else if (activeTab === 4) {
            filtered = filtered.filter((order) => order.status === 'Cancelled');
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (order) =>
                    order.poNumber.toLowerCase().includes(term) ||
                    order.supplierName?.toLowerCase().includes(term) ||
                    order.supplierNameAr?.toLowerCase().includes(term) ||
                    order.employeeName?.toLowerCase().includes(term) ||
                    order.employeeNameAr?.toLowerCase().includes(term) ||
                    order.paymentAccountName?.toLowerCase().includes(term) ||
                    order.paymentAccountNameAr?.toLowerCase().includes(term)
            );
        }

        if (filterDateFrom) {
            filtered = filtered.filter(o => o.poDate >= filterDateFrom);
        }
        if (filterDateTo) {
            filtered = filtered.filter(o => o.poDate <= filterDateTo);
        }

        setFilteredOrders(filtered);
    };

    const handleAdd = () => {
        setSelectedOrder(undefined);
        setDialogOpen(true);
    };

    const handleView = async (order: PurchaseOrder) => {
        handleCloseMenu();
        try {
            const fullOrder = await purchaseOrdersService.getById(
                order.purchaseOrderId
            );
            setOrderToPreview(fullOrder);
            setPreviewDialogOpen(true);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to load order details');
        }
    };

    const handleEdit = (order: PurchaseOrder) => {
        setSelectedOrder(order);
        setDialogOpen(true);
        handleCloseMenu();
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
        if (!ok) { handleCloseMenu(); return; }
        try {
            await purchaseOrdersService.delete(id);
            loadOrders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete');
        }
        handleCloseMenu();
    };

    const handleApprove = async (id: number) => {
        const ok = await confirm({ message: t.confirmApprove, severity: 'warning' });
        if (!ok) { handleCloseMenu(); return; }
        try {
            await purchaseOrdersService.updateStatus(id, 'Approved');
            loadOrders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to approve');
        }
        handleCloseMenu();
    };

    const handleComplete = async (id: number) => {
        const ok = await confirm({ message: t.confirmComplete, severity: 'warning' });
        if (!ok) { handleCloseMenu(); return; }
        try {
            await purchaseOrdersService.updateStatus(id, 'Received');
            loadOrders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to complete');
        }
        handleCloseMenu();
    };

    const handleCancelOrder = async (id: number) => {
        const ok = await confirm({ message: t.confirmCancel, severity: 'error' });
        if (!ok) { handleCloseMenu(); return; }
        try {
            await purchaseOrdersService.updateStatus(id, 'Cancelled');
            loadOrders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to cancel');
        }
        handleCloseMenu();
    };

    const handleConvertClick = (order: PurchaseOrder) => {
        setOrderToConvert(order);
        setConvertDialogOpen(true);
        handleCloseMenu();
    };

    const handleConvertConfirm = async () => {
        if (!orderToConvert) return;

        try {
            const result = await purchaseOrdersService.convertToInvoice(
                orderToConvert.purchaseOrderId
            );
            toast.success(
                language === 'ar'
                    ? `تم تحويل الأمر إلى فاتورة شراء بنجاح! رقم الفاتورة: ${result.invoiceId}`
                    : `Order converted to invoice successfully! Invoice ID: ${result.invoiceId}`
            );
            setConvertDialogOpen(false);
            setOrderToConvert(null);
            loadOrders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to convert');
        }
    };

    const handleOpenMenu = (
        event: React.MouseEvent<HTMLElement>,
        order: PurchaseOrder
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrderForMenu(order);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedOrderForMenu(null);
    };

    const calculateStats = () => {
        const total = orders.length;
        const totalAmount = orders.reduce(
            (sum, order) => sum + (order.totalAmount || 0),
            0
        );
        const pendingCount = orders.filter(
            (order) => order.status === 'Draft' || order.status === 'Approved'
        ).length;
        const draftCount = orders.filter(
            (order) => order.status === 'Draft'
        ).length;
        const approvedCount = orders.filter(
            (order) => order.status === 'Approved'
        ).length;
        const receivedCount = orders.filter(
            (order) => order.status === 'Received'
        ).length;
        const cancelledCount = orders.filter(
            (order) => order.status === 'Cancelled'
        ).length;

        return {
            total,
            totalAmount,
            pendingCount,
            draftCount,
            approvedCount,
            receivedCount,
            cancelledCount,
        };
    };

    const stats = calculateStats();

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusIcon = (status?: string) => {
        switch (status) {
            case 'Draft':
                return <ScheduleIcon />;
            case 'Approved':
                return <CheckCircleIcon />;
            case 'Received':
                return <LocalShippingIcon />;
            case 'Cancelled':
                return <CancelIcon />;
            default:
                return <ScheduleIcon />;
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'Draft':
                return 'warning';
            case 'Approved':
                return 'info';
            case 'Received':
                return 'success';
            case 'Cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    const getPaymentSourceDisplay = (order: PurchaseOrder) => {
        if (order.supplierId) {
            return {
                icon: <BusinessIcon fontSize="small" />,
                label: t.supplier,
                name: isRTL ? order.supplierNameAr : order.supplierName,
                color: 'primary' as const,
            };
        } else if (order.custodyId) {
            return {
                icon: <PersonIcon fontSize="small" />,
                label: t.custody,
                name: isRTL ? order.employeeNameAr : order.employeeName,
                color: 'secondary' as const,
            };
        } else if (order.paymentAccountId) {
            return {
                icon: <AccountIcon fontSize="small" />,
                label: t.paymentAccount,
                name: isRTL
                    ? order.paymentAccountNameAr
                    : order.paymentAccountName,
                color: 'info' as const,
            };
        }
        return null;
    };

    return (
        <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        sx={{
                            width: 56,
                            height: 56,
                            background:
                                'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        }}
                    >
                        <ShoppingCartIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {t.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {stats.total} {language === 'ar' ? 'أمر' : 'orders'}
                        </Typography>
                    </Box>
                </Stack>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadOrders} color="primary"><RefreshIcon /></IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        size="large"
                        sx={{
                            background:
                                'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)',
                            boxShadow: '0 3px 5px 2px rgba(240, 147, 251, .3)',
                            px: 4,
                            py: 1.5,
                            '&:hover': {
                                background:
                                    'linear-gradient(45deg, #e082ea 30%, #e4465b 90%)',
                            },
                        }}
                    >
                        {t.addOrder}
                    </Button>
                </Box>
            </Stack>

            {/* Stats Cards */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)',
                    },
                    gap: 2,
                    mb: 3,
                }}
            >
                <Card
                    sx={{
                        background:
                            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -20,
                            right: -20,
                            width: 100,
                            height: 100,
                            background:
                                'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                            borderRadius: '50%',
                        },
                    }}
                >
                    <CardContent>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.9 }}
                                >
                                    {t.totalOrders}
                                </Typography>
                                <Typography
                                    variant="h3"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {stats.total}
                                </Typography>
                            </Box>
                            <ShoppingCartIcon
                                sx={{ fontSize: 50, opacity: 0.3 }}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background:
                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.9 }}
                                >
                                    {t.totalAmount}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {stats.totalAmount.toFixed(3)}
                                </Typography>
                                <Typography variant="caption">KD</Typography>
                            </Box>
                            <TrendingUpIcon
                                sx={{ fontSize: 50, opacity: 0.3 }}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background:
                            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.9 }}
                                >
                                    {t.pendingOrders}
                                </Typography>
                                <Typography
                                    variant="h3"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {stats.pendingCount}
                                </Typography>
                            </Box>
                            <ScheduleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
                        </Stack>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background:
                            'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <CardContent>
                        <Stack spacing={0.5}>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                {t.status}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {stats.draftCount}
                                    </Typography>
                                    <Typography variant="caption">
                                        {t.draft}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {stats.approvedCount}
                                    </Typography>
                                    <Typography variant="caption">
                                        {t.approved}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {stats.receivedCount}
                                    </Typography>
                                    <Typography variant="caption">
                                        {t.received}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {stats.cancelledCount}
                                    </Typography>
                                    <Typography variant="caption">
                                        {t.cancelled}
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>

            {/* Filters */}
            <Paper
                elevation={3}
                sx={{
                    mb: 3,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)'
                            : 'linear-gradient(135deg, #fce7f3 0%, #ddd6fe 100%)',
                    borderRadius: 3,
                }}
            >
                <Stack spacing={2} sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        placeholder={t.search}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="primary" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={isRTL ? 'من تاريخ' : 'From Date'}
                                value={filterDateFrom ? new Date(filterDateFrom) : null}
                                onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateFrom(v); }}
                                format="dd/MM/yyyy"
                                minDate={minDate}
                                maxDate={filterDateTo ? new Date(filterDateTo) : maxDate}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />
                            <DatePicker
                                label={isRTL ? 'إلى تاريخ' : 'To Date'}
                                value={filterDateTo ? new Date(filterDateTo) : null}
                                onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateTo(v); }}
                                format="dd/MM/yyyy"
                                minDate={filterDateFrom ? new Date(filterDateFrom) : minDate}
                                maxDate={maxDate}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />
                        </LocalizationProvider>
                        {(filterDateFrom || filterDateTo) && (
                            <Button size="small" onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}>{isRTL ? 'مسح' : 'Clear'}</Button>
                        )}
                    </Stack>

                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: 48,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                            },
                            '& .Mui-selected': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    >
                        <Tab
                            label={
                                <Badge
                                    badgeContent={stats.total}
                                    color="primary"
                                    max={999}
                                >
                                    <span>{t.all}</span>
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={stats.draftCount}
                                    color="warning"
                                    max={999}
                                >
                                    <span>{t.draft}</span>
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={stats.approvedCount}
                                    color="info"
                                    max={999}
                                >
                                    <span>{t.approved}</span>
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={stats.receivedCount}
                                    color="success"
                                    max={999}
                                >
                                    <span>{t.received}</span>
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={stats.cancelledCount}
                                    color="error"
                                    max={999}
                                >
                                    <span>{t.cancelled}</span>
                                </Badge>
                            }
                        />
                    </Tabs>
                </Stack>
            </Paper>

            {/* Orders List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} />
                </Box>
            ) : (
                <Stack spacing={2}>
                    {paginatedOrders.map((order, index) => {
                        const paymentSource = getPaymentSourceDisplay(order);

                        return (
                                <Card
                                    key={order.purchaseOrderId}
                                    elevation={3}
                                    sx={{
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 8,
                                        },
                                        border: 2,
                                        borderColor: `${getStatusColor(order.status)}.light`,
                                        borderLeft: 6,
                                        borderLeftColor: `${getStatusColor(order.status)}.main`,
                                        background:
                                            theme.palette.mode === 'dark'
                                                ? alpha('#1e293b', 0.8)
                                                : '#ffffff',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                mb: 2,
                                            }}
                                        >
                                            {/* Order Header */}
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        bgcolor: `${getStatusColor(order.status)}.main`,
                                                    }}
                                                >
                                                    {getStatusIcon(
                                                        order.status
                                                    )}
                                                </Avatar>
                                                <Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: 'primary.main',
                                                        }}
                                                    >
                                                        {order.poNumber}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {formatDate(
                                                            order.poDate
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            {/* Status & Menu */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <Chip
                                                    label={
                                                        t[
                                                            order.status?.toLowerCase() as keyof typeof t
                                                        ] || order.status
                                                    }
                                                    color={
                                                        getStatusColor(
                                                            order.status
                                                        ) as any
                                                    }
                                                    icon={getStatusIcon(
                                                        order.status
                                                    )}
                                                    sx={{
                                                        fontWeight: 600,
                                                        minWidth: 100,
                                                    }}
                                                />

                                                <IconButton
                                                    onClick={(e) =>
                                                        handleOpenMenu(e, order)
                                                    }
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Stack>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Order Details */}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr',
                                                    md: 'repeat(2, 1fr)',
                                                    lg: 'repeat(4, 1fr)',
                                                },
                                                gap: 3,
                                            }}
                                        >
                                            {/* Payment Source */}
                                            {paymentSource && (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {t.paymentSource}
                                                    </Typography>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {paymentSource.icon}
                                                        <Box>
                                                            <Typography
                                                                variant="caption"
                                                                color={`${paymentSource.color}.main`}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {
                                                                    paymentSource.label
                                                                }
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {
                                                                    paymentSource.name
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            )}

                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {t.warehouse}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {isRTL
                                                        ? order.warehouseNameAr
                                                        : order.warehouseName}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {t.delivery}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {order.expectedDeliveryDate
                                                        ? formatDate(
                                                              order.expectedDeliveryDate
                                                          )
                                                        : '-'}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {t.amount}
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    {order.totalAmount?.toFixed(
                                                        3
                                                    )}{' '}
                                                    KD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                        );
                    })}

                    {filteredOrders.length === 0 && (
                        <Paper
                            sx={{
                                p: 8,
                                textAlign: 'center',
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.02
                                ),
                            }}
                        >
                            <ShoppingCartIcon
                                sx={{
                                    fontSize: 80,
                                    color: 'text.disabled',
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h6" color="text.secondary">
                                {t.noData}
                            </Typography>
                        </Paper>
                    )}

                    {filteredOrders.length > 0 && (
                        <Paper elevation={2} sx={{ mt: 1 }}>
                            <TablePagination
                                component="div"
                                count={filteredOrders.length}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage={isRTL ? 'صفوف في الصفحة:' : 'Rows per page:'}
                                labelDisplayedRows={({ from, to, count }) =>
                                    isRTL ? `${from}–${to} من ${count}` : `${from}–${to} of ${count}`
                                }
                                sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
                            />
                        </Paper>
                    )}
                </Stack>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        minWidth: 200,
                        mt: 1,
                    },
                }}
            >
                <MenuItem
                    onClick={() =>
                        selectedOrderForMenu && handleView(selectedOrderForMenu)
                    }
                >
                    <ViewIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                    <Typography color="primary.main">{t.view}</Typography>
                </MenuItem>

                <Divider />

                {selectedOrderForMenu?.status === 'Draft' &&
                    canEditOrder(selectedOrderForMenu) && (
                        <MenuItem
                            onClick={() => handleEdit(selectedOrderForMenu)}
                        >
                            <EditIcon fontSize="small" sx={{ mr: 1 }} />
                            {t.edit}
                        </MenuItem>
                    )}

                {selectedOrderForMenu?.status === 'Draft' &&
                    canEditOrder(selectedOrderForMenu) && (
                        <MenuItem
                            onClick={() =>
                                handleDelete(
                                    selectedOrderForMenu.purchaseOrderId
                                )
                            }
                        >
                            <DeleteIcon
                                fontSize="small"
                                sx={{ mr: 1 }}
                                color="error"
                            />
                            <Typography color="error">{t.delete}</Typography>
                        </MenuItem>
                    )}

                {selectedOrderForMenu?.status === 'Draft' &&
                    canApproveOrder() && (
                        <MenuItem
                            onClick={() =>
                                handleApprove(
                                    selectedOrderForMenu.purchaseOrderId
                                )
                            }
                        >
                            <ApproveIcon
                                fontSize="small"
                                sx={{ mr: 1 }}
                                color="info"
                            />
                            <Typography color="info.main">
                                {t.approve}
                            </Typography>
                        </MenuItem>
                    )}

                {selectedOrderForMenu?.status === 'Approved' &&
                    canApproveOrder() && (
                        <MenuItem
                            onClick={() =>
                                handleComplete(
                                    selectedOrderForMenu.purchaseOrderId
                                )
                            }
                        >
                            <CheckCircleIcon
                                fontSize="small"
                                sx={{ mr: 1 }}
                                color="success"
                            />
                            <Typography color="success.main">
                                {t.complete}
                            </Typography>
                        </MenuItem>
                    )}

                {(selectedOrderForMenu?.status === 'Draft' ||
                    selectedOrderForMenu?.status === 'Approved') &&
                    canApproveOrder() && (
                        <MenuItem
                            onClick={() =>
                                handleCancelOrder(
                                    selectedOrderForMenu.purchaseOrderId
                                )
                            }
                        >
                            <CancelIcon
                                fontSize="small"
                                sx={{ mr: 1 }}
                                color="error"
                            />
                            <Typography color="error">{t.cancel}</Typography>
                        </MenuItem>
                    )}

                <Divider />

                {selectedOrderForMenu?.status === 'Received' &&
                    !selectedOrderForMenu?.hasInvoice &&
                    canApproveOrder() && (
                        <MenuItem
                            onClick={() =>
                                handleConvertClick(selectedOrderForMenu)
                            }
                        >
                            <ConvertIcon
                                fontSize="small"
                                sx={{ mr: 1 }}
                                color="primary"
                            />
                            <Typography color="primary.main">
                                {t.convertToInvoice}
                            </Typography>
                        </MenuItem>
                    )}
            </Menu>

            {/* Convert Dialog */}
            <Dialog
                open={convertDialogOpen}
                onClose={() => setConvertDialogOpen(false)}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ConvertIcon color="primary" />
                        <span>{t.convertToInvoice}</span>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography>{t.convertConfirm}</Typography>
                    {orderToConvert && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 2,
                            }}
                        >
                            {(() => {
                                const paymentSource =
                                    getPaymentSourceDisplay(orderToConvert);
                                return paymentSource ? (
                                    <Stack spacing={1}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t.paymentSource}:{' '}
                                            <strong>
                                                {paymentSource.label}
                                            </strong>
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {paymentSource.name}
                                        </Typography>
                                    </Stack>
                                ) : null;
                            })()}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                            >
                                {t.amount}:{' '}
                                <strong>
                                    {orderToConvert.totalAmount?.toFixed(3)} KD
                                </strong>
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConvertDialogOpen(false)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConvertConfirm}
                        startIcon={<ConvertIcon />}
                    >
                        {t.convertToInvoice}
                    </Button>
                </DialogActions>
            </Dialog>

            <PurchaseOrderDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                purchaseOrder={selectedOrder}
                onSave={loadOrders}
            />

            <PurchaseOrderPreviewDialog
                open={previewDialogOpen}
                onClose={() => {
                    setPreviewDialogOpen(false);
                    setOrderToPreview(null);
                }}
                purchaseOrder={orderToPreview}
                onEdit={() => {
                    if (orderToPreview) {
                        setPreviewDialogOpen(false);
                        handleEdit(orderToPreview);
                    }
                }}
            />
        </Box>
    );
};

export default PurchaseOrdersPage;